// Shared utility for displaying session details in a new window
// Used by both CostAnalysis and TrackUserActivity components

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';

import { AttemptDetails, ImplicitAttemptRecord } from '../types';
import { Auth } from '../utils';
import { apiClient, PLAYGROUND, PLATFORM } from '../services/apiClient';
import { getUserDetailsById } from '../services/user';

type FeedbackEntry = { question: string; answer: string };

export interface SessionData {
    attemptId: string;
    socketId?: string | null;
    status?: string;
    costs: {
        totalCost: number;
        realtime: {
            totalCost: number;
        };
        geminiLive?: {
            totalCost: number;
        };
        tutor: {
            totalCost: number;
        };
        implicitChecker?: {
            totalCost: number;
            inputCost?: number;
            outputCost?: number;
            callCount?: number;
            tokens?: {
                image?: number;
                reasoning?: number;
                textOutput?: number;
                prompt?: number;
            };
        };
    };
    duration: string;
    startTime: string;
    endTime?: string;
    messageCount?: number;
    implicitAttemptCount?: number;
    attemptUserId?: string | null;
    attemptDetails?: AttemptDetails | null;
    user?: {
        id: string;
        name: string;
        email: string;
        grade?: string;
    };
    questionFeedback?: FeedbackEntry[];
    questionFeedbackMeta?: {
        difficultyLevel?: string | null;
        createdAt?: number | string | null;
        updatedAt?: number | string | null;
    } | null;
}

export const formatCost = (cost: number) => `$${cost.toFixed(2)}`;

const STORAGE_BUCKET_NAME = 'diraq-contents';
const PLATFORM_BASE_URL = (process.env.REACT_APP_PLATFORM_URL || '').replace(/\/$/, '');
const LANGFUSE_TUTOR_PROJECT_ID = process.env.REACT_APP_LANGFUSE_TUTOR_PROJECT_ID || '';
const AUTH_COOKIE_NAME = Auth.TOKEN;

const humanizeLabel = (value: string): string => {
    if (!value) return '—';
    const words = value
        .replace(/[_\s]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
    const result = words.join(' ');
    return result.replace(/\bAi\b/g, 'AI');
};

type SessionViewerRoot = Document | ShadowRoot;

const MESSAGE_TIMEZONE = 'Asia/Kolkata';

const toMillis = (value: any): number | null => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const numeric = Number(value);
        if (!Number.isNaN(numeric) && Number.isFinite(numeric)) return numeric;
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object') {
        if (typeof (value as any).toNumber === 'function') {
            try { return (value as any).toNumber(); } catch (_) { return null; }
        }
        if (typeof (value as any).valueOf === 'function') {
            const raw = (value as any).valueOf();
            if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
        }
    }
    return null;
};

const formatTimestamp = (value: any): string => {
    const millis = toMillis(value);
    if (millis == null) return 'Unknown';
    try {
        return new Date(millis).toLocaleString('en-IN', { timeZone: MESSAGE_TIMEZONE });
    } catch (_) {
        return 'Unknown';
    }
};

const formatMessageTimestamp = (message: any): string =>
    formatTimestamp(message?.createdAt ?? message?.timestamp);

const deriveTimestamp = (msg: any): number | null => {
    if (!msg) return null;
    if (msg?.createdAt != null) {
        return typeof msg.createdAt === 'number'
            ? msg.createdAt
            : new Date(msg.createdAt).getTime();
    }
    if (msg?.timestamp != null) {
        return typeof msg.timestamp === 'number'
            ? msg.timestamp
            : new Date(msg.timestamp).getTime();
    }
    return null;
};

const formatDurationLabel = (startTime: number | null, endTime: number | null): string => {
    if (!startTime || !endTime) return '0s';
    const seconds = Math.round(Math.abs(endTime - startTime) / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const normalizeAttemptId = (value: any) => String(value ?? '').trim();

type SessionViewerOptions = {
    platformBaseUrl?: string;
    authCookieName?: string;
    bucketName?: string;
};

export interface SessionBundle {
    session: SessionData;
    conversation: any[];
    implicitAttempts: ImplicitAttemptRecord[];
}

const mathDelimPattern = /\$\$|\$|\\\(|\\\)|\\\[|\\\]|\\begin\{.*?}|\\end\{.*?}/;
const mathCommandPattern = /\\[a-zA-Z]+|[_^]{1,2}/;

const normalizeBoardContent = (raw: string): string => {
    const lines = raw.split(/\r?\n/);
    const processed: string[] = [];

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
            processed.push('');
            continue;
        }

        const hasDelimiter = mathDelimPattern.test(trimmed);
        const hasMathTokens = mathCommandPattern.test(trimmed);

        if (hasDelimiter) {
            processed.push(trimmed);
            continue;
        }

        if (hasMathTokens) {
            // Treat standalone lines containing math tokens as display math blocks.
            processed.push(`$$${trimmed}$$`);
            continue;
        }

        processed.push(trimmed);
    }

    return processed.join('\n');
};

const renderBoardMarkdown = (value: string): string => {
    if (!value || !value.trim()) return '';
    const normalized = normalizeBoardContent(value);
    try {
        return renderToStaticMarkup(
            React.createElement(ReactMarkdown, {
                children: normalized,
                remarkPlugins: [remarkMath],
                rehypePlugins: [rehypeKatex],
            })
        );
    } catch (err) {
        console.warn('[sessionViewer] Failed to render board markdown', err);
        return escapeHtml(value).replace(/\n/g, '<br />');
    }
};

const ensureCosts = (costs?: SessionData['costs']): SessionData['costs'] => ({
    totalCost: costs?.totalCost ?? 0,
    realtime: {
        totalCost: costs?.realtime?.totalCost ?? 0,
    },
    geminiLive: {
        totalCost: costs?.geminiLive?.totalCost ?? 0,
    },
    tutor: {
        totalCost: costs?.tutor?.totalCost ?? 0,
    },
    implicitChecker: costs?.implicitChecker
        ? {
            totalCost: costs.implicitChecker.totalCost ?? 0,
            inputCost: costs.implicitChecker.inputCost,
            outputCost: costs.implicitChecker.outputCost,
            callCount: costs.implicitChecker.callCount,
            tokens: costs.implicitChecker.tokens,
        }
        : {
            totalCost: 0,
        },
});

const mergeAttemptDetails = (
    summaryDetails?: AttemptDetails | null,
    payloadDetails?: AttemptDetails | null
): AttemptDetails | null => {
    if (summaryDetails && payloadDetails) {
        const merged: AttemptDetails = { ...payloadDetails, ...summaryDetails };
        if (merged.fermiAnalysis == null && payloadDetails.fermiAnalysis != null) {
            merged.fermiAnalysis = payloadDetails.fermiAnalysis;
        }
        if (merged.validationMetaData == null && payloadDetails.validationMetaData != null) {
            merged.validationMetaData = payloadDetails.validationMetaData;
        }
        if (merged.totalTimeSpent == null && payloadDetails.totalTimeSpent != null) {
            merged.totalTimeSpent = payloadDetails.totalTimeSpent;
        }
        if (merged.createdAt == null && payloadDetails.createdAt != null) {
            merged.createdAt = payloadDetails.createdAt;
        }
        if (merged.updatedAt == null && payloadDetails.updatedAt != null) {
            merged.updatedAt = payloadDetails.updatedAt;
        }
        return merged;
    }
    return summaryDetails || payloadDetails || null;
};

export async function loadSessionBundle(attemptIdRaw: string, serverUrl: string, environment?: string): Promise<SessionBundle> {
    const attemptId = normalizeAttemptId(attemptIdRaw);
    if (!attemptId) {
        throw new Error('No session ID provided');
    }

    if (environment) {
        console.log('[sessionViewer] loadSessionBundle: using environment', environment);
    }

    // Fetch session cost summary for this specific attempt only
    const summaryEndpoint = `/api/sessions/summary/${attemptId}`;
    console.log('[sessionViewer] loadSessionBundle: fetching session summary', { summaryEndpoint, attemptId, environment });
    let session: SessionData | null = null;

    try {
        const summary = await apiClient.get<any>(summaryEndpoint, PLAYGROUND);
        session = {
            ...summary,
            costs: ensureCosts(summary?.costs),
        } as SessionData;
    } catch (summaryErr: any) {
        if (summaryErr?.status !== 404) {
            console.warn('[sessionViewer] loadSessionBundle: failed to fetch summary', { status: summaryErr?.status });
        }
        console.warn('[sessionViewer] loadSessionBundle: summary fetch failed', summaryErr);
    }

    const convEndpoint = `/api/conversations/${attemptId}`;
    console.log('[sessionViewer] loadSessionBundle: fetching conversation', { convEndpoint, environment });
    let payload: any = null;
    try {
        payload = await apiClient.get<any>(convEndpoint, PLAYGROUND);
        console.log('[sessionViewer] loadSessionBundle: conversation response success');
    } catch (conversationErr) {
        console.warn('[sessionViewer] loadSessionBundle: conversation fetch failed', conversationErr);
    }

    const conversation = Array.isArray(payload?.conversation)
        ? payload.conversation
        : Array.isArray(payload)
            ? payload
            : [];
    const implicitAttempts = Array.isArray(payload?.implicitAttempts) ? payload.implicitAttempts : [];
    const attemptDetailsFromPayload = payload?.attempt || null;

    type FeedbackEntry = { question: string; answer: string };
    const normalizeFeedbackEntry = (entry: any): FeedbackEntry | null => {
        if (!entry || typeof entry !== 'object') return null;
        const questionText = entry.question != null ? String(entry.question) : '';
        const answerText = entry.answer != null ? String(entry.answer) : '';
        if (!questionText && !answerText) return null;
        return { question: questionText, answer: answerText };
    };

    const questionFeedbackDoc = payload?.questionFeedback || null;
    const feedbackEntries = questionFeedbackDoc && Array.isArray(questionFeedbackDoc.feedbackQuestions)
        ? questionFeedbackDoc.feedbackQuestions
            .map(normalizeFeedbackEntry)
            .filter((item: FeedbackEntry | null): item is FeedbackEntry => Boolean(item))
        : [];
    const feedbackMeta = questionFeedbackDoc
        ? {
            difficultyLevel: questionFeedbackDoc.difficultyLevel ?? null,
            createdAt: questionFeedbackDoc.createdAt ?? null,
            updatedAt: questionFeedbackDoc.updatedAt ?? null,
        }
        : null;

    console.log('[sessionViewer] loadSessionBundle: payload processed', {
        conversationCount: Array.isArray(conversation) ? conversation.length : 0,
        implicitAttempts: implicitAttempts.length,
    });

    const first = Array.isArray(conversation) ? conversation.find((m: any) => deriveTimestamp(m) != null) : null;
    const last = Array.isArray(conversation)
        ? [...conversation].reverse().find((m: any) => deriveTimestamp(m) != null)
        : null;
    const startTime = first ? deriveTimestamp(first) : null;
    const endTime = last ? deriveTimestamp(last) : null;
    const duration = formatDurationLabel(startTime, endTime);

    const resolvedAttemptId = normalizeAttemptId(
        session?.attemptId
        || conversation.find((msg: any) => msg?.attemptId)?.attemptId
        || payload?.attemptId
        || attemptId
    );

    const socketIdFromData = payload?.socketId
        || conversation.find((msg: any) => msg?.socketId)?.socketId
        || implicitAttempts.find((entry: any) => entry?.socket_id)?.socket_id
        || session?.socketId;

    if (session && session.costs && !session.costs.implicitChecker) {
        session.costs.implicitChecker = { totalCost: 0 };
    }

    const attemptDetailsFromSummary = session?.attemptDetails || null;
    const mergedAttemptDetails = mergeAttemptDetails(attemptDetailsFromSummary, attemptDetailsFromPayload);
    const attemptUserIdFromPayload = payload?.attemptUserId ? String(payload.attemptUserId) : null;
    const statusFromPayload = typeof payload?.status === 'string' && payload.status.trim() !== '' ? payload.status : null;
    const attemptUserIdFallback = mergedAttemptDetails && (mergedAttemptDetails as any).userId
        ? String((mergedAttemptDetails as any).userId)
        : null;
    // Prefer userId from questionattempts collection (most reliable, always present),
    // then session summary, then attempt details, then conversation messages as last resort
    const conversationUserId = Array.isArray(conversation)
        ? conversation.find((msg: any) => msg && msg.userId)?.userId || null
        : null;
    const derivedAttemptUserId = attemptUserIdFromPayload || session?.attemptUserId || attemptUserIdFallback || conversationUserId;

    let userInfo: any = null;
    if (derivedAttemptUserId) {
        try {
            console.log('[sessionViewer] loadSessionBundle: fetching user info via platform-node', { userId: derivedAttemptUserId });
            userInfo = await getUserDetailsById(derivedAttemptUserId);
        } catch (userErr: any) {
            console.warn('[sessionViewer] loadSessionBundle: user fetch error', userErr);
        }
    }
    const fallbackStatusFromDetails = mergedAttemptDetails
        ? (mergedAttemptDetails as any).isValid === true ? 'COMPLETED' : 'IN_PROGRESS'
        : 'Unknown';

    if (!session) {
        session = {
            attemptId: resolvedAttemptId,
            socketId: socketIdFromData,
            status: statusFromPayload || (mergedAttemptDetails && (mergedAttemptDetails as any).resolvedStatus)
                || (mergedAttemptDetails && (mergedAttemptDetails as any).status)
                || fallbackStatusFromDetails,
            attemptUserId: derivedAttemptUserId || undefined,
            attemptDetails: mergedAttemptDetails || undefined,
            costs: ensureCosts(),
            duration,
            startTime: startTime ? new Date(startTime).toLocaleString() : 'Unknown',
            endTime: endTime ? new Date(endTime).toLocaleString() : 'Unknown',
            messageCount: Array.isArray(conversation) ? conversation.length : 0,
            implicitAttemptCount: implicitAttempts.length,
            user: userInfo ? {
                id: userInfo._id || userInfo.id,
                name: userInfo.name,
                email: userInfo.email,
                grade: userInfo.grade,
            } : undefined,
            questionFeedback: feedbackEntries,
            questionFeedbackMeta: feedbackMeta,
        };
    } else {
        session.attemptId = resolvedAttemptId;
        if (socketIdFromData) session.socketId = socketIdFromData;
        session.costs = ensureCosts(session.costs);
        session.implicitAttemptCount = implicitAttempts.length;
        if (mergedAttemptDetails) {
            session.attemptDetails = mergedAttemptDetails;
        }
        if (statusFromPayload) {
            session.status = statusFromPayload;
        } else if (mergedAttemptDetails) {
            session.status = fallbackStatusFromDetails;
        }
        if (derivedAttemptUserId) {
            session.attemptUserId = derivedAttemptUserId;
        }
        if (typeof session.duration !== 'string' || !session.duration) {
            session.duration = duration;
        }
        if (!session.startTime) {
            session.startTime = startTime ? new Date(startTime).toLocaleString() : 'Unknown';
        }
        if (!session.endTime) {
            session.endTime = endTime ? new Date(endTime).toLocaleString() : 'Unknown';
        }
        if (session.messageCount == null) {
            session.messageCount = Array.isArray(conversation) ? conversation.length : 0;
        }
        if (session.user && userInfo) {
            session.user = {
                id: userInfo._id || userInfo.id,
                name: userInfo.name,
                email: userInfo.email,
                grade: userInfo.grade,
            };
        }
        session.questionFeedback = feedbackEntries;
        session.questionFeedbackMeta = feedbackMeta;
    }

    if (session && !session.user && userInfo) {
        session.user = {
            id: userInfo._id || userInfo.id,
            name: userInfo.name,
            email: userInfo.email,
            grade: userInfo.grade,
        };
    }

    if (!session.questionFeedback) {
        session.questionFeedback = feedbackEntries;
    }
    if (!session.questionFeedbackMeta) {
        session.questionFeedbackMeta = feedbackMeta;
    }
    session.costs = ensureCosts(session.costs);
    session.duration = session.duration || duration;
    session.startTime = session.startTime || (startTime ? new Date(startTime).toLocaleString() : 'Unknown');
    session.endTime = session.endTime || (endTime ? new Date(endTime).toLocaleString() : 'Unknown');

    return { session, conversation, implicitAttempts };
}

export function attachSessionViewerInteractions(
    root: SessionViewerRoot,
    options: SessionViewerOptions = {}
): () => void {
    const query = <T extends Element>(selector: string) => root.querySelector(selector) as T | null;

    const sessionHeader = query<HTMLElement>('#sessionDetailsHeader');
    const sessionContent = query<HTMLElement>('#sessionDetailsContent');
    const sessionIcon = query<HTMLElement>('#sessionToggleIcon');
    const filterToggle = query<HTMLInputElement>('#filterToggle');
    const refreshButton = query<HTMLButtonElement>('#refreshSolutionImages');
    const refreshStatus = query<HTMLElement>('#refreshImageStatus');
    const containerEl = query<HTMLElement>('.container');
    const implicitSlideTrack = query<HTMLElement>('#implicitSlideTrack');
    const implicitPrevButton = query<HTMLButtonElement>('#implicitFlowPrev');
    const implicitNextButton = query<HTMLButtonElement>('#implicitFlowNext');
    const implicitStatusLabel = query<HTMLElement>('#implicitFlowStatus');
    const implicitFilterToggle = query<HTMLInputElement>('#implicitIssuesOnly');
    const implicitSuppressRejectedToggle = query<HTMLInputElement>('#implicitSuppressRejected');
    const implicitEmptyState = query<HTMLElement>('#implicitFilterEmptyState');
    const ownerDocument = root instanceof Document ? root : root.ownerDocument;

    const resolvedPlatformBaseUrl = (options.platformBaseUrl
        ?? containerEl?.getAttribute('data-platform-url')
        ?? '').replace(/\/$/, '').trim();
    const resolvedAuthCookieName = (options.authCookieName
        ?? containerEl?.getAttribute('data-auth-cookie')
        ?? AUTH_COOKIE_NAME).trim();
    const resolvedBucketName = options.bucketName
        ?? containerEl?.getAttribute('data-bucket-name')
        ?? STORAGE_BUCKET_NAME;

    const getCookie = (name: string) => {
        if (!name || typeof document === 'undefined') return '';
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${encodeURIComponent(name)}=`);
        if (parts.length === 2) {
            return decodeURIComponent(parts.pop()?.split(';').shift() || '');
        }
        const rawParts = value.split(`; ${name}=`);
        if (rawParts.length === 2) {
            return decodeURIComponent(rawParts.pop()?.split(';').shift() || '');
        }
        return '';
    };

    const setRefreshStatus = (message: string, tone: 'neutral' | 'success' | 'error') => {
        if (!refreshStatus) return;
        refreshStatus.textContent = message;
        refreshStatus.classList.remove('refresh-success', 'refresh-error');
        if (tone === 'success') {
            refreshStatus.classList.add('refresh-success');
        } else if (tone === 'error') {
            refreshStatus.classList.add('refresh-error');
        }
    };

    const extractFileName = (url: string): string | null => {
        if (!url) return null;
        try {
            const urlObj = new URL(url, window.location.origin);
            const withoutQuery = urlObj.origin === 'null'
                ? url.split('?')[0]
                : `${urlObj.origin}${urlObj.pathname}`;
            const trimmed = withoutQuery.split('?')[0];
            const marker = `${resolvedBucketName}/`;
            const markerIndex = trimmed.indexOf(marker);
            if (markerIndex === -1) return null;
            const startIndex = markerIndex + marker.length;
            const fileName = trimmed.slice(startIndex);
            return fileName ? decodeURIComponent(fileName) : null;
        } catch (_) {
            const cleaned = url.split('?')[0];
            const marker = `${resolvedBucketName}/`;
            const markerIndex = cleaned.indexOf(marker);
            if (markerIndex === -1) return null;
            const startIndex = markerIndex + marker.length;
            const raw = cleaned.slice(startIndex);
            return raw ? decodeURIComponent(raw) : null;
        }
    };

    const getDownloadUrl = async (fileName: string, authToken: string): Promise<string | null> => {
        if (!resolvedPlatformBaseUrl || !fileName || !authToken) {
            return null;
        }
        const endpoint = `${resolvedPlatformBaseUrl}/file/download-url`;
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ fileName, bucketName: resolvedBucketName })
            });
            if (!response.ok) {
                console.warn('[sessionViewer] download-url request failed', response.status, response.statusText);
                return null;
            }
            const data = await response.json();
            return data?.data?.downloadUrl || null;
        } catch (error) {
            console.error('[sessionViewer] download-url request error', error);
            return null;
        }
    };

    const refreshSolutionImages = async () => {
        if (!refreshButton) return;
        if (!resolvedPlatformBaseUrl) {
            setRefreshStatus('Platform URL missing.', 'error');
            return;
        }
        const token = resolvedAuthCookieName ? getCookie(resolvedAuthCookieName) : '';
        if (!token) {
            setRefreshStatus('Auth token missing; login required.', 'error');
            return;
        }

        const imageNodes = Array.from(root.querySelectorAll<HTMLImageElement>('img[data-solution-image="true"]'));
        if (!imageNodes.length) {
            setRefreshStatus('No solution images found.', 'neutral');
            return;
        }

        refreshButton.disabled = true;
        refreshButton.classList.add('is-loading');
        setRefreshStatus('Refreshing images...', 'neutral');

        const imageToFile = new Map<HTMLImageElement, string>();
        const uniqueFileNames: string[] = [];
        const seen = new Set<string>();
        for (const img of imageNodes) {
            const source = img.currentSrc || img.src;
            const fileName = extractFileName(source);
            if (!fileName) continue;
            imageToFile.set(img, fileName);
            if (!seen.has(fileName)) {
                seen.add(fileName);
                uniqueFileNames.push(fileName);
            }
        }

        if (!uniqueFileNames.length) {
            refreshButton.disabled = false;
            refreshButton.classList.remove('is-loading');
            setRefreshStatus('No refreshable images.', 'neutral');
            return;
        }

        const results = new Map<string, string>();
        const failures: string[] = [];

        await Promise.all(uniqueFileNames.map(async (fileName) => {
            const newUrl = await getDownloadUrl(fileName, token);
            if (newUrl) {
                results.set(fileName, newUrl);
            } else {
                failures.push(fileName);
            }
        }));

        imageToFile.forEach((fileName, img) => {
            const freshUrl = results.get(fileName);
            if (freshUrl) {
                img.src = freshUrl;
            }
        });

        refreshButton.disabled = false;
        refreshButton.classList.remove('is-loading');

        if (failures.length === uniqueFileNames.length) {
            setRefreshStatus('Failed to refresh images.', 'error');
        } else if (failures.length) {
            setRefreshStatus('Images refreshed with some failures.', 'error');
        } else {
            setRefreshStatus('Images refreshed.', 'success');
        }
    };

    const handleSessionToggle = () => {
        if (!sessionContent || !sessionIcon) return;
        const isCollapsed = sessionContent.classList.contains('collapsed');
        if (isCollapsed) {
            sessionContent.classList.remove('collapsed');
            sessionIcon.textContent = '▼';
        } else {
            sessionContent.classList.add('collapsed');
            sessionIcon.textContent = '▶';
        }
    };

    const handleFilterChange = (event: Event) => {
        const input = event.target as HTMLInputElement | null;
        if (!input) return;
        const messages = root.querySelectorAll<HTMLElement>('.message');
        messages.forEach((msg) => {
            const messageType = msg.getAttribute('data-message-type');
            const shouldHide = input.checked && (messageType === 'function_call' || messageType === 'intent_call');
            msg.classList.toggle('is-hidden', shouldHide);
        });
    };

    sessionHeader?.addEventListener('click', handleSessionToggle);
    filterToggle?.addEventListener('change', handleFilterChange);
    refreshButton?.addEventListener('click', refreshSolutionImages);

    let cleanupSlider: (() => void) | undefined;
    if (implicitSlideTrack) {
        const slides = Array.from(implicitSlideTrack.querySelectorAll<HTMLElement>('.implicit-slide'));
        if (slides.length) {
            let activeIndex = 0;

            const getVisibleSlides = () => slides.filter((slide) => !slide.classList.contains('is-filtered'));
            const updateSlider = () => {
                const visibleSlides = getVisibleSlides();
                if (!visibleSlides.length) {
                    slides.forEach((slide) => slide.classList.remove('is-active'));
                    if (implicitStatusLabel) {
                        implicitStatusLabel.textContent = '0 / 0';
                    }
                    if (implicitPrevButton) {
                        implicitPrevButton.disabled = true;
                    }
                    if (implicitNextButton) {
                        implicitNextButton.disabled = true;
                    }
                    if (implicitEmptyState) {
                        implicitEmptyState.hidden = false;
                    }
                    return;
                }

                if (activeIndex >= visibleSlides.length) {
                    activeIndex = visibleSlides.length - 1;
                }

                slides.forEach((slide) => slide.classList.remove('is-active'));
                visibleSlides.forEach((slide, index) => {
                    if (index === activeIndex) {
                        slide.classList.add('is-active');
                    }
                });

                if (implicitStatusLabel) {
                    implicitStatusLabel.textContent = `${activeIndex + 1} / ${visibleSlides.length}`;
                }
                if (implicitPrevButton) {
                    implicitPrevButton.disabled = visibleSlides.length <= 1 || activeIndex === 0;
                }
                if (implicitNextButton) {
                    implicitNextButton.disabled = visibleSlides.length <= 1 || activeIndex === visibleSlides.length - 1;
                }
                if (implicitEmptyState) {
                    implicitEmptyState.hidden = true;
                }
            };

            const goTo = (index: number) => {
                const visibleSlides = getVisibleSlides();
                if (!visibleSlides.length) return;
                const clamped = Math.max(0, Math.min(index, visibleSlides.length - 1));
                if (clamped === activeIndex) return;
                activeIndex = clamped;
                updateSlider();
            };

            const handlePrevSlide = () => goTo(activeIndex - 1);
            const handleNextSlide = () => goTo(activeIndex + 1);
            const handleSliderFilterChange = () => {
                const requireIssues = Boolean(implicitFilterToggle?.checked);
                const suppressRejected = Boolean(implicitSuppressRejectedToggle?.checked);
                slides.forEach((slide) => {
                    const hasFeedback = slide.getAttribute('data-has-feedback') === 'true';
                    const isShown = slide.getAttribute('data-shown') === 'true';
                    const hideByIssuesFilter = requireIssues && !hasFeedback;
                    const hideByShownFilter = suppressRejected && !isShown;
                    const shouldHide = hideByIssuesFilter || hideByShownFilter;
                    slide.classList.toggle('is-filtered', shouldHide);
                });
                activeIndex = 0;
                updateSlider();
            };
            const handleSliderKeydown = (event: KeyboardEvent) => {
                if (event.defaultPrevented) return;
                if (event.key === 'ArrowLeft') {
                    handlePrevSlide();
                    event.preventDefault();
                } else if (event.key === 'ArrowRight') {
                    handleNextSlide();
                    event.preventDefault();
                }
            };

            implicitPrevButton?.addEventListener('click', handlePrevSlide);
            implicitNextButton?.addEventListener('click', handleNextSlide);
            implicitFilterToggle?.addEventListener('change', handleSliderFilterChange);
            implicitSuppressRejectedToggle?.addEventListener('change', handleSliderFilterChange);
            ownerDocument?.addEventListener('keydown', handleSliderKeydown);
            handleSliderFilterChange();  // Apply default filter state (suppress rejected is checked by default)
            updateSlider();

            cleanupSlider = () => {
                implicitPrevButton?.removeEventListener('click', handlePrevSlide);
                implicitNextButton?.removeEventListener('click', handleNextSlide);
                implicitFilterToggle?.removeEventListener('change', handleSliderFilterChange);
                implicitSuppressRejectedToggle?.removeEventListener('change', handleSliderFilterChange);
                ownerDocument?.removeEventListener('keydown', handleSliderKeydown);
            };
        }
    }

    return () => {
        sessionHeader?.removeEventListener('click', handleSessionToggle);
        filterToggle?.removeEventListener('change', handleFilterChange);
        refreshButton?.removeEventListener('click', refreshSolutionImages);
        cleanupSlider?.();
    };
}

export function attachAttemptViewerInteractions(root: SessionViewerRoot): () => void {
    const slides = Array.from(root.querySelectorAll<HTMLElement>('.flow-slide'));
    const prevBtn = root.querySelector<HTMLButtonElement>('#flowNavPrev');
    const nextBtn = root.querySelector<HTMLButtonElement>('#flowNavNext');
    const statusLabel = root.querySelector<HTMLElement>('#flowNavStatus');

    let activeIndex = 0;

    const update = () => {
        const total = slides.length;
        slides.forEach((slide, index) => {
            const isActive = index === activeIndex;
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        if (statusLabel) {
            statusLabel.textContent = total ? `${activeIndex + 1} / ${total}` : '0 / 0';
        }
        if (prevBtn) {
            prevBtn.disabled = total <= 1 || activeIndex === 0;
        }
        if (nextBtn) {
            nextBtn.disabled = total <= 1 || activeIndex === total - 1;
        }
    };

    const goTo = (index: number) => {
        if (!slides.length) return;
        const clamped = Math.max(0, Math.min(index, slides.length - 1));
        if (clamped === activeIndex) return;
        activeIndex = clamped;
        update();
    };

    const handlePrev = () => goTo(activeIndex - 1);
    const handleNext = () => goTo(activeIndex + 1);
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.defaultPrevented) return;
        if (event.key === 'ArrowLeft') {
            handlePrev();
        } else if (event.key === 'ArrowRight') {
            handleNext();
        } else {
            return;
        }
        event.preventDefault();
    };

    prevBtn?.addEventListener('click', handlePrev);
    nextBtn?.addEventListener('click', handleNext);
    const ownerDocument = root instanceof Document ? root : root.ownerDocument;
    ownerDocument?.addEventListener('keydown', handleKeyDown);

    update();

    return () => {
        prevBtn?.removeEventListener('click', handlePrev);
        nextBtn?.removeEventListener('click', handleNext);
        ownerDocument?.removeEventListener('keydown', handleKeyDown);
    };
}

function buildSessionInteractionsScript(
    platformBaseUrl: string,
    authCookieName: string,
    bucketName: string
): string {
    const fnSrc = attachSessionViewerInteractions.toString();
    const platformLiteral = JSON.stringify(platformBaseUrl);
    const authLiteral = JSON.stringify(authCookieName);
    const bucketLiteral = JSON.stringify(bucketName);
    return `
(function() {
    const attach = ${fnSrc};
    const run = () => {
        attach(document, {
            platformBaseUrl: ${platformLiteral},
            authCookieName: ${authLiteral},
            bucketName: ${bucketLiteral}
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
        run();
    }
})();
    `;
}

function buildAttemptViewerInteractionsScript(): string {
    const fnSrc = attachAttemptViewerInteractions.toString();
    return `
(function() {
    const attach = ${fnSrc};
    const run = () => {
        attach(document);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
        run();
    }
})();
    `;
}

function escapeHtml(value: any): string {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const renderImplicitRichText = (value: any): string => {
    const raw = value == null ? '' : typeof value === 'string' ? value : String(value);
    if (!raw.trim()) {
        return '<div class="implicit-rich-text">—</div>';
    }

    const rendered = renderBoardMarkdown(raw);
    if (rendered && rendered.trim()) {
        return `<div class="implicit-rich-text markdown-body">${rendered}</div>`;
    }
    return `<div class="implicit-rich-text">${escapeHtml(raw).replace(/\n/g, '<br />')}</div>`;
};

const hasImplicitIssueContent = (record: ImplicitAttemptRecord | null | undefined): boolean => {
    if (!record) return false;
    const fields = [record.correction, record.hint, record.error];
    return fields.some((value) => {
        if (value == null) return false;
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        return String(value).trim().length > 0;
    });
};

function renderImplicitAttempts(records: ImplicitAttemptRecord[], formatTimestamp: (value: any) => string): string {
    if (!Array.isArray(records) || records.length === 0) {
        return '<div class="implicit-empty">No implicit checker activity recorded for this attempt.</div>';
    }

    const formatBoolean = (value: any) => (value === undefined || value === null ? '—' : value ? 'Yes' : 'No');
    const stringify = (input: any) => {
        try {
            return JSON.stringify(input, null, 2);
        } catch (_) {
            return String(input ?? '');
        }
    };

    const slidesHtml = records.map((record, index) => {
        const createdLabel = formatTimestamp(record.createdAt ?? record.updatedAt);
        const questionImg = record.question_url ? `
            <div class="implicit-image">
                <div class="implicit-subtitle">Question Image</div>
                <img src="${record.question_url}" alt="Implicit question" />
            </div>
        ` : '';
        const solutionImg = record.solution_url ? `
            <div class="implicit-image">
                <div class="implicit-subtitle">Solution Image</div>
                <img data-solution-image="true" src="${record.solution_url}" alt="Implicit solution" />
            </div>
        ` : '';
        const timeTakenBlock = record.time_taken ? `
            <div class="implicit-text-block">
                <div class="implicit-subtitle">Timing (ms)</div>
                <pre class="code-block">${stringify(record.time_taken)}</pre>
            </div>
        ` : '';
        const boundingBoxBlock = record.bounding_box ? `
            <div class="implicit-text-block">
                <div class="implicit-subtitle">Bounding Box</div>
                <pre class="code-block">${stringify(record.bounding_box)}</pre>
            </div>
        ` : '';
        const correctionHtml = renderImplicitRichText(record.correction);
        const hintHtml = renderImplicitRichText(record.hint);
        const hasIssues = hasImplicitIssueContent(record);

        const entryHtml = `
            <div class="implicit-entry">
                <div class="implicit-header">
                    <div class="implicit-title">Step ${index + 1}</div>
                    <div class="implicit-meta">
                        <span>Attempt ID: <code>${record.question_attempt_id || '—'}</code></span>
                        ${record.socket_id ? `<span>Socket: <code>${record.socket_id}</code></span>` : ''}
                        <span>Recorded: ${createdLabel}</span>
                    </div>
                </div>
                <div class="implicit-body">
                    <div class="implicit-info-grid">
                        <div class="implicit-info-item"><span class="implicit-label">Job</span><span class="implicit-value">${record.job_id || '—'}</span></div>
                        <div class="implicit-info-item"><span class="implicit-label">Shown to User</span><span class="implicit-value">${formatBoolean(record.shown)}</span></div>
                        <div class="implicit-info-item"><span class="implicit-label">Solution Complete</span><span class="implicit-value">${formatBoolean(record.solution_complete)}</span></div>
                    </div>
                    <div class="implicit-text-block">
                        <div class="implicit-subtitle">Checker Response (y)</div>
                        <pre class="code-block">${record.y || '—'}</pre>
                    </div>
                    <div class="implicit-text-block">
                        <div class="implicit-subtitle">Error Line Number</div>
                        <pre class="code-block">${record.error_line_number != null ? record.error_line_number : '—'}</pre>
                    </div>
                    <div class="implicit-text-grid">
                        <div class="implicit-text-block">
                            <div class="implicit-subtitle">Correction</div>
                            ${correctionHtml}
                        </div>
                        <div class="implicit-text-block">
                            <div class="implicit-subtitle">Hint</div>
                            ${hintHtml}
                        </div>
                    </div>
                    ${record.error ? `
                        <div class="implicit-text-block">
                            <div class="implicit-subtitle">Error</div>
                            <pre class="code-block">${record.error}</pre>
                        </div>
                    ` : ''}
                    ${(questionImg || solutionImg) ? `
                        <div class="implicit-image-grid">
                            ${questionImg}
                            ${solutionImg}
                        </div>
                    ` : ''}
                    ${timeTakenBlock}
                    ${boundingBoxBlock}
                </div>
            </div>
        `;

        return `
            <div class="implicit-slide${index === 0 ? ' is-active' : ''}" data-index="${index}" data-has-feedback="${hasIssues ? 'true' : 'false'}" data-shown="${record.shown === false ? 'false' : 'true'}">
                ${entryHtml}
            </div>
        `;
    }).join('');

    const initialStatus = records.length ? `1 / ${records.length}` : '0 / 0';
    const navDisabledAttr = records.length <= 1 ? 'disabled' : '';

    return `
        <div class="implicit-slider" id="implicitSlidesRoot">
            <div class="implicit-slider-bar">
                <div class="implicit-flow-nav">
                    <button type="button" class="implicit-flow-btn" id="implicitFlowPrev" aria-label="Previous implicit attempt" ${navDisabledAttr}>&larr;</button>
                    <div class="implicit-flow-status" id="implicitFlowStatus">${initialStatus}</div>
                    <button type="button" class="implicit-flow-btn" id="implicitFlowNext" aria-label="Next implicit attempt" ${navDisabledAttr}>&rarr;</button>
                </div>
                <div class="implicit-filter-controls">
                    <label class="implicit-filter-toggle">
                        <input type="checkbox" id="implicitIssuesOnly">
                        <span>Show only steps with error, hint, or correction</span>
                    </label>
                    <label class="implicit-filter-toggle">
                        <input type="checkbox" id="implicitSuppressRejected" checked>
                        <span>Suppress rejected checks</span>
                    </label>
                </div>
            </div>
            <div class="implicit-slide-track" id="implicitSlideTrack">
                ${slidesHtml}
            </div>
            <div class="implicit-filter-empty" id="implicitFilterEmptyState" hidden>No implicit attempts match this filter.</div>
        </div>
    `;
}

const normalizeAnalysisText = (value: any): string => {
    if (value === null || value === undefined) return '';
    const raw = typeof value === 'string' ? value : String(value);
    return raw.trim();
};

const toTrimmedStringArray = (value: any): string[] => {
    if (!Array.isArray(value)) return [];
    return value
        .map((entry) => normalizeAnalysisText(entry))
        .filter((entry) => entry.length > 0);
};

const renderAnalysisTextSection = (label: string, value: any): string => {
    const text = normalizeAnalysisText(value);
    if (!text) return '';
    return `
        <div class="analysis-section">
            <div class="analysis-section-label">${escapeHtml(label)}</div>
            <div class="analysis-section-body">${escapeHtml(text).replace(/\n/g, '<br />')}</div>
        </div>
    `;
};

const renderAnalysisListSection = (label: string, value: any): string => {
    const entries = toTrimmedStringArray(value);
    if (!entries.length) return '';
    return `
        <div class="analysis-section">
            <div class="analysis-section-label">${escapeHtml(label)}</div>
            <ul class="analysis-list">
                ${entries.map((entry) => `<li>${escapeHtml(entry)}</li>`).join('')}
            </ul>
        </div>
    `;
};

const renderAnalysisHighlight = (value: any): string => {
    const text = normalizeAnalysisText(value);
    if (!text) return '';
    return `<div class="analysis-highlight">${escapeHtml(text)}</div>`;
};

const buildAnalysisCard = (title: string, highlight: string | null, sections: string[]): string => {
    const parts = [highlight, ...sections]
        .filter((section) => typeof section === 'string' && section.trim().length > 0);
    if (!parts.length) return '';
    return `
        <div class="analysis-card">
            <div class="analysis-card-title">${escapeHtml(title)}</div>
            ${parts.join('')}
        </div>
    `;
};

export function generateAttemptViewerHTML(
    session: SessionData,
    conversation: any[],
    implicitAttempts: ImplicitAttemptRecord[] = []
): string {
    const attemptDetails = session?.attemptDetails || null;
    const resolvedStatusRaw = attemptDetails?.resolvedStatus
        || attemptDetails?.status
        || session?.status
        || '';
    const resolvedStatus = typeof resolvedStatusRaw === 'string' ? resolvedStatusRaw.trim() : '';
    const statusUpper = resolvedStatus.toUpperCase();
    const isCorrect = statusUpper === 'CORRECT';
    const fallbackStatus = typeof session?.status === 'string' ? session.status.trim() : '';
    const statusLabel = resolvedStatus ? humanizeLabel(resolvedStatus) : (fallbackStatus ? humanizeLabel(fallbackStatus) : 'Status Unknown');
    const badgeText = isCorrect ? 'Valid' : statusLabel;
    const badgeTitleParts = [`status: ${statusUpper || fallbackStatus || 'Unknown'}`];
    if (attemptDetails?.betaValidation != null) {
        badgeTitleParts.push(`betaValidation: ${attemptDetails.betaValidation ? 'true' : 'false'}`);
    }
    if (attemptDetails?.validationMetaData) {
        badgeTitleParts.push(`validationMetaData: ${JSON.stringify(attemptDetails.validationMetaData, null, 2)}`);
    }
    const badgeTitle = escapeHtml(badgeTitleParts.join('\n')).replace(/\n/g, '&#10;');
    const showBadge = Boolean(resolvedStatus || fallbackStatus);
    const validityBadgeHtml = showBadge
        ? `<span class="validity-badge ${isCorrect ? 'valid' : 'invalid'}" title="${badgeTitle}"><span class="badge-dot"></span>${escapeHtml(badgeText)}</span>`
        : '';

    const allowedTypes = new Set(['user', 'assistant', 'supervisor']);
    const filteredConversation = Array.isArray(conversation)
        ? conversation.filter((message: any) => {
            const type = typeof message?.type === 'string' ? message.type.toLowerCase() : '';
            return allowedTypes.has(type);
        })
        : [];

    const lastWithQuestion = Array.isArray(conversation)
        ? [...conversation].reverse().find((m: any) => m && m.questionUrl)
        : null;
    const lastWithSolution = Array.isArray(conversation)
        ? [...conversation].reverse().find((m: any) => m && m.solutionUrl)
        : null;
    const lastImplicitQuestion = Array.isArray(implicitAttempts)
        ? [...implicitAttempts].reverse().find((a: ImplicitAttemptRecord) => a && a.question_url)
        : null;
    const lastImplicitSolution = Array.isArray(implicitAttempts)
        ? [...implicitAttempts].reverse().find((a: ImplicitAttemptRecord) => a && a.solution_url)
        : null;
    const questionImageUrl = lastWithQuestion?.questionUrl || lastImplicitQuestion?.question_url || '';
    const solutionImageUrl = lastWithSolution?.solutionUrl || lastImplicitSolution?.solution_url || '';

    const formatMultiline = (value: any) => escapeHtml(String(value ?? '')).replace(/\n/g, '<br />');

    const questionImageHtml = questionImageUrl
        ? `<img src="${escapeHtml(questionImageUrl)}" alt="Question image" class="main-image">`
        : '<div class="image-placeholder">No question image available.</div>';

    const solutionImageHtml = solutionImageUrl
        ? `<img src="${escapeHtml(solutionImageUrl)}" alt="Student solution image" class="main-image" data-solution-image="true">`
        : '<div class="image-placeholder">No student solution image available.</div>';

    const conversationItemsHtml = filteredConversation.map((message: any) => {
        const typeRaw = typeof message?.type === 'string' ? message.type.toLowerCase() : '';
        const roleClass = typeRaw.replace(/[^a-z0-9_-]/g, '') || 'message';
        const roleLabel = typeRaw === 'user'
            ? 'Student'
            : typeRaw
                ? humanizeLabel(typeRaw)
                : 'Message';
        const messageText = message?.text ? `<div class="conv-text">${formatMultiline(message.text)}</div>` : '';
        const boardText = (() => {
            if (message?.boardText == null) return '';
            const raw = typeof message.boardText === 'string'
                ? message.boardText
                : String(message.boardText);
            if (!raw.trim()) return '';
            const rendered = renderBoardMarkdown(raw);
            if (!rendered.trim()) return '';
            return `
                <div class="conv-board">
                    <span class="conv-subtitle">Board</span>
                    <div class="conv-board-content markdown-body">${rendered}</div>
                </div>
            `;
        })();
        const attachments: string[] = [];
        if (message?.questionUrl) {
            attachments.push(`
                <figure class="conv-attachment">
                    <span>Question</span>
                    <img src="${escapeHtml(message.questionUrl)}" alt="Question image">
                </figure>
            `);
        }
        if (message?.solutionUrl) {
            attachments.push(`
                <figure class="conv-attachment">
                    <span>Solution</span>
                    <img src="${escapeHtml(message.solutionUrl)}" alt="Solution image" data-solution-image="true">
                </figure>
            `);
        }
        const attachmentsHtml = attachments.length ? `<div class="conv-attachments">${attachments.join('')}</div>` : '';
        const bodyContent = [messageText, boardText, attachmentsHtml].filter(Boolean).join('');
        const content = bodyContent || '<div class="conv-text conv-empty">No content for this message.</div>';
        return `
            <article class="conv-item conv-${roleClass}">
                <header class="conv-header">
                    <span class="conv-role">${escapeHtml(roleLabel)}</span>
                    <span class="conv-time">${escapeHtml(formatMessageTimestamp(message))}</span>
                </header>
                ${content}
            </article>
        `;
    }).join('');

    const conversationContent = filteredConversation.length
        ? `<div class="conversation-list">${conversationItemsHtml}</div>`
        : '<div class="empty-state">No conversation messages to display.</div>';

    const slidesHtml = Array.isArray(implicitAttempts) && implicitAttempts.length
        ? implicitAttempts.map((record, index) => {
            const timestampLabel = formatTimestamp(record.createdAt ?? record.updatedAt);
            const solutionMedia = record.solution_url
                ? `<img class="flow-image" src="${escapeHtml(record.solution_url)}" alt="Implicit attempt ${index + 1} solution image" data-solution-image="true">`
                : '<div class="flow-image-placeholder">No solution image for this attempt.</div>';
            return `
                <div class="flow-slide${index === 0 ? ' is-active' : ''}" data-index="${index}" aria-hidden="${index === 0 ? 'false' : 'true'}">
                    <div class="flow-slide-header">
                        <div class="flow-slide-title">Step ${index + 1}</div>
                        <div class="flow-slide-meta">${escapeHtml(timestampLabel)}</div>
                    </div>
                    <div class="flow-slide-body">
                        ${solutionMedia}
                    </div>
                </div>
            `;
        }).join('')
        : '';

    const initialStatus = implicitAttempts.length ? `1 / ${implicitAttempts.length}` : '0 / 0';
    const navButtonState = implicitAttempts.length <= 1 ? 'disabled' : '';
    const flowContent = implicitAttempts.length
        ? `
            <div class="flow-wrapper">
                <div class="flow-nav-bar">
                    <button type="button" class="flow-nav-btn" id="flowNavPrev" aria-label="Previous implicit attempt" ${navButtonState}>&larr;</button>
                    <div class="flow-status" id="flowNavStatus">${initialStatus}</div>
                    <button type="button" class="flow-nav-btn" id="flowNavNext" aria-label="Next implicit attempt" ${navButtonState}>&rarr;</button>
                </div>
                <div class="flow-slides">
                    ${slidesHtml}
                </div>
            </div>
        `
        : '<div class="flow-empty">No implicit attempts recorded for this session.</div>';

    const userName = session?.user?.name ? escapeHtml(session.user.name) : 'Unknown Learner';

    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Session Viewer - ${escapeHtml(session.attemptId || session.socketId || 'Attempt')}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css" />
            <style>
                body {
                    margin: 0;
                    padding: 0;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
                    color: #0f172a;
                }
                .viewer-container {
                    max-width: 1100px;
                    margin: 0 auto;
                    padding: 32px 24px 48px;
                }
                .viewer-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    margin-bottom: 28px;
                }
                .viewer-title {
                    margin: 0;
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.02em;
                }
                .validity-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 14px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fecaca;
                }
                .validity-badge.valid {
                    background: #dcfce7;
                    color: #15803d;
                    border-color: #bbf7d0;
                }
                .badge-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: currentColor;
                    display: inline-block;
                }
                .viewer-section {
                    background: #ffffff;
                    border-radius: 20px;
                    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
                    padding: 24px;
                    margin-bottom: 24px;
                }
                .section-title {
                    margin: 0 0 12px;
                    font-size: 18px;
                    font-weight: 700;
                    color: #111827;
                }
                .section-text {
                    margin: 0 0 16px;
                    font-size: 15px;
                    line-height: 1.6;
                    color: #334155;
                }
                .image-card {
                    background: #f1f5f9;
                    border-radius: 16px;
                    padding: 16px;
                    min-height: 240px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #e2e8f0;
                }
                .main-image {
                    max-width: 100%;
                    border-radius: 12px;
                    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
                }
                .image-placeholder {
                    font-size: 14px;
                    color: #64748b;
                }
                .conversation-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    gap: 16px;
                    margin-bottom: 16px;
                }
                .conversation-note {
                    font-size: 12px;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                }
                .conversation-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .conv-item {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 16px;
                    transition: box-shadow 0.2s ease, border-color 0.2s ease;
                }
                .conv-item:hover {
                    border-color: #c7d2fe;
                    box-shadow: 0 10px 20px rgba(99, 102, 241, 0.1);
                }
                .conv-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .conv-role {
                    font-size: 13px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #4338ca;
                }
                .conv-item.conv-user .conv-role { color: #2563eb; }
                .conv-item.conv-assistant .conv-role { color: #7c3aed; }
                .conv-item.conv-supervisor .conv-role { color: #0ea5e9; }
                .conv-time {
                    font-size: 12px;
                    color: #94a3b8;
                }
                .conv-text {
                    font-size: 14px;
                    line-height: 1.6;
                    color: #1e293b;
                }
                .conv-empty {
                    font-size: 13px;
                    color: #94a3b8;
                    font-style: italic;
                }
                .conv-board {
                    margin-top: 12px;
                }
                .conv-subtitle {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #475569;
                    margin-bottom: 6px;
                }
                .conv-board-content {
                    margin: 0;
                    background: #111827;
                    color: #e2e8f0;
                    padding: 12px;
                    border-radius: 10px;
                    font-size: 14px;
                    line-height: 1.6;
                    overflow-x: auto;
                }
                .conv-board-content p {
                    margin: 0 0 10px;
                }
                .conv-board-content p:last-child {
                    margin-bottom: 0;
                }
                .conv-attachments {
                    margin-top: 12px;
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .conv-attachment {
                    flex: 1 1 220px;
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    padding: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .conv-attachment span {
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                }
                .conv-attachment img {
                    width: 100%;
                    border-radius: 10px;
                    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
                }
                .empty-state {
                    text-align: center;
                    padding: 20px;
                    border-radius: 12px;
                    background: #f1f5f9;
                    color: #64748b;
                    font-size: 14px;
                }
                .flow-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .flow-nav-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                .flow-nav-btn {
                    background: #6366f1;
                    border: none;
                    color: #ffffff;
                    border-radius: 999px;
                    width: 42px;
                    height: 42px;
                    font-size: 18px;
                    font-weight: 700;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s ease, transform 0.2s ease;
                }
                .flow-nav-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }
                .flow-nav-btn:disabled {
                    background: #c7d2fe;
                    color: #475569;
                    cursor: not-allowed;
                    transform: none;
                }
                .flow-status {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                    min-width: 80px;
                    text-align: center;
                }
                .flow-slides {
                    position: relative;
                    min-height: 320px;
                }
                .flow-slide {
                    display: none;
                    flex-direction: column;
                    gap: 20px;
                }
                .flow-slide.is-active {
                    display: flex;
                }
                .flow-slide-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    gap: 16px;
                }
                .flow-slide-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #111827;
                }
                .flow-slide-meta {
                    font-size: 12px;
                    color: #94a3b8;
                }
                .flow-slide-body {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    align-items: center;
                }
                .flow-image {
                    max-width: 100%;
                    border-radius: 16px;
                    background: #eef2ff;
                    border: 1px solid #c7d2fe;
                    box-shadow: 0 10px 20px rgba(79, 70, 229, 0.18);
                }
                .flow-image-placeholder {
                    padding: 40px 20px;
                    text-align: center;
                    border-radius: 16px;
                    border: 1px dashed #cbd5f5;
                    color: #94a3b8;
                    font-size: 14px;
                    background: #f8fafc;
                }
                .flow-empty {
                    text-align: center;
                    padding: 32px 16px;
                    border-radius: 16px;
                    background: #f8fafc;
                    border: 1px dashed #dbeafe;
                    color: #64748b;
                    font-size: 14px;
                }
                @media (max-width: 768px) {
                    .viewer-container {
                        padding: 24px 16px 40px;
                    }
                    .viewer-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .viewer-title {
                        font-size: 24px;
                    }
                }
            </style>
        </head>
        <body>
            <div class="viewer-container">
                <header class="viewer-header">
                    <h1 class="viewer-title">Student Name : ${userName}</h1>
                    ${validityBadgeHtml}
                </header>
                <section class="viewer-section">
                    <h2 class="section-title">Question</h2>
                    <div class="image-card">
                        ${questionImageHtml}
                    </div>
                </section>
                <section class="viewer-section">
                    <h2 class="section-title">Student Solution</h2>
                    <div class="image-card">
                        ${solutionImageHtml}
                    </div>
                </section>
                <section class="viewer-section">
                    <h2 class="section-title">Student Flow</h2>
                    ${flowContent}
                </section>
                <section class="viewer-section">
                    <div class="conversation-header">
                        <h2 class="section-title">Conversation</h2>
                        <span class="conversation-note">User · Supervisor · Assistant</span>
                    </div>
                    ${conversationContent}
                </section>
            </div>
            <script>
${buildAttemptViewerInteractionsScript()}
            </script>
        </body>
        </html>
    `;
}

export function generateSessionHTML(
    session: SessionData,
    conversation: any[],
    implicitAttempts: ImplicitAttemptRecord[] = []
): string {
    // Build detailed HTML for conversation including board text, attachments, and tool/intent data
    // Find latest question/solution images for Overview
    const lastWithQuestion = Array.isArray(conversation)
        ? [...conversation].reverse().find((m: any) => m && m.questionUrl)
        : null;
    const lastWithSolution = Array.isArray(conversation)
        ? [...conversation].reverse().find((m: any) => m && m.solutionUrl)
        : null;
    const lastImplicitQuestion = Array.isArray(implicitAttempts)
        ? [...implicitAttempts].reverse().find((a: ImplicitAttemptRecord) => a && a.question_url)
        : null;
    const lastImplicitSolution = Array.isArray(implicitAttempts)
        ? [...implicitAttempts].reverse().find((a: ImplicitAttemptRecord) => a && a.solution_url)
        : null;
    const latestQuestionUrl = lastWithQuestion?.questionUrl || lastImplicitQuestion?.question_url || '';
    const latestSolutionUrl = lastWithSolution?.solutionUrl || lastImplicitSolution?.solution_url || '';
    const attemptDetails = session.attemptDetails || null;
    const attemptStatusLabel = attemptDetails?.resolvedStatus || attemptDetails?.status || 'Unknown';
    const isAttemptValid = attemptDetails?.isValid === true;
    const validationMetadataString = attemptDetails?.validationMetaData != null
        ? JSON.stringify(attemptDetails.validationMetaData, null, 2)
        : null;
    const validationMetadataHtml = validationMetadataString != null
        ? `
            <div class="validation-item validation-metadata-item">
                <span class="validation-label">Validation Metadata:</span>
                <pre class="validation-metadata">${escapeHtml(validationMetadataString)}</pre>
            </div>
        `
        : `
            <div class="validation-item">
                <span class="validation-label">Validation Metadata:</span>
                <span class="validation-value">None</span>
            </div>
        `;

    const conversationHtml = Array.isArray(conversation) && conversation.length > 0 ? conversation.map((message: any) => {
        const data = message?.data || {};
        const output = data?.output || {};
        return `
            <div class="message ${message?.type || ''}" data-message-type="${message?.type || ''}">
                <div class="message-header">
                    <span class="message-type">${message?.type || ''}</span>
                    <span class="message-timestamp">${formatMessageTimestamp(message)}</span>
                </div>
                ${message?.text ? `<div class="message-text">${message.text}</div>` : ''}
                ${(message?.boardText != null && String(message.boardText).trim() !== '') ? (() => {
                    const rawBoard = typeof message.boardText === 'string'
                        ? message.boardText
                        : String(message.boardText);
                    const renderedBoard = renderBoardMarkdown(rawBoard);
                    if (!renderedBoard.trim()) return '';
                    return `
                    <div class="message-board">
                        <div class="message-section-title">Board</div>
                        <div class="message-board-content markdown-body">${renderedBoard}</div>
                    </div>
                `;
                })() : ''}
                ${(message?.questionUrl || message?.solutionUrl) ? `
                    <div style="display: flex; gap: 16px; margin-top: 8px;">
                        ${message?.questionUrl ? `
                            <details style="flex: 1;">
                                <summary style="cursor: pointer; color: #667eea; font-weight: 600; font-size: 14px; padding: 6px 0;">
                                    📷 Question
                                </summary>
                                <img src="${message.questionUrl}" style="max-width: 100%; height: auto; margin-top: 8px; border-radius: 6px; border: 1px solid #e2e8f0; display: block;">
                            </details>
                        ` : ''}
                        ${message?.solutionUrl ? `
                            <details style="flex: 1;">
                                <summary style="cursor: pointer; color: #667eea; font-weight: 600; font-size: 14px; padding: 6px 0;">
                                    📷 Solution
                                </summary>
                                <img data-solution-image="true" src="${message.solutionUrl}" style="max-width: 100%; height: auto; margin-top: 8px; border-radius: 6px; border: 1px solid #e2e8f0; display: block;">
                            </details>
                        ` : ''}
                    </div>
                ` : ''}
                ${(message?.type === 'function_call' && message?.data) ? `
                    <div class="message-extra">
                        <div class="message-section-title">Function Result</div>
                        ${(data?.status || data?.duration != null) ? `
                            <div class="kv">
                                ${data?.status ? `<div class="kv-row"><span class="kv-label">Status:</span><span class="kv-value">${data.status}</span></div>` : ''}
                                ${data?.duration != null ? `<div class="kv-row"><span class="kv-label">Duration:</span><span class="kv-value">${data.duration}s</span></div>` : ''}
                            </div>
                        ` : ''}
                        ${(output?.text_content || output?.audio_content) ? `
                            <div class="output-blocks">
                                ${output?.text_content ? `
                                    <div class="output-text">
                                        <div class="message-subtitle">Text Content</div>
                                        <pre class="code-block">${output.text_content}</pre>
                                    </div>
                                ` : ''}
                                ${output?.audio_content ? `
                                    <div class="output-audio">
                                        <div class="message-subtitle">Audio Content</div>
                                        <pre class="code-block">${output.audio_content}</pre>
                                    </div>
                                ` : ''}
                            </div>
                        ` : ''}
                    </div>
                ` : ''}
                ${(message?.type === 'intent_call' && message?.data) ? `
                    <div class="message-extra">
                        <div class="message-section-title">Intent</div>
                        <div class="kv">
                            ${data?.intent ? `<div class="kv-row"><span class="kv-label">Intent:</span><span class="kv-value">${data.intent}</span></div>` : ''}
                            ${data?.duration != null ? `<div class="kv-row"><span class="kv-label">Duration:</span><span class="kv-value">${data.duration}s</span></div>` : ''}
                        </div>
                        ${data?.userMessage ? `<div class="intent-msg"><span class="intent-badge user">User</span><span class="intent-text">${data.userMessage}</span></div>` : ''}
                        ${data?.assistantMessage ? `<div class="intent-msg"><span class="intent-badge assistant">Assistant</span><span class="intent-text">${data.assistantMessage}</span></div>` : ''}
                    </div>
                ` : ''}
                ${(message?.type === 'supervisor' && message?.tutorMetadata) ? (() => {
                    const metadata = message.tutorMetadata;
                    const hasBasicFields = metadata.isFollowUp != null || metadata.rapsSteps || metadata.hintLevel != null || metadata.toneToUse;
                    const hasReasoningFields = metadata.reasoningForAnswer || metadata.guidanceLevelReasoning;
                    const hasGuidanceSteps = Array.isArray(metadata.guidanceSteps) && metadata.guidanceSteps.length > 0;
                    const hasShouldFillCanvasReason = metadata.shouldFillCanvasReason;
                    const hasIsMathsInAudio = metadata.isMathsInAudio != null;
                    const hasReasoningSummary = metadata.reasoningSummary;

                    if (!hasBasicFields && !hasReasoningFields && !hasGuidanceSteps && !hasShouldFillCanvasReason && !hasIsMathsInAudio && !hasReasoningSummary) {
                        return '';
                    }

                    return `
                    <div class="supervisor-metadata">
                        <div class="supervisor-metadata-title">Tutor Metadata</div>
                        ${hasBasicFields ? `
                            <div class="supervisor-metadata-grid">
                                ${metadata.isFollowUp != null ? `
                                    <div class="supervisor-metadata-item">
                                        <div class="supervisor-metadata-label">Is Follow Up</div>
                                        <div class="supervisor-metadata-value">${metadata.isFollowUp ? 'Yes' : 'No'}</div>
                                    </div>
                                ` : ''}
                                ${metadata.rapsSteps ? `
                                    <div class="supervisor-metadata-item">
                                        <div class="supervisor-metadata-label">RAPS Steps</div>
                                        <div class="supervisor-metadata-value">${escapeHtml(metadata.rapsSteps)}</div>
                                    </div>
                                ` : ''}
                                ${metadata.hintLevel != null ? `
                                    <div class="supervisor-metadata-item">
                                        <div class="supervisor-metadata-label">Hint Level</div>
                                        <div class="supervisor-metadata-value">${metadata.hintLevel}</div>
                                    </div>
                                ` : ''}
                                ${metadata.toneToUse ? `
                                    <div class="supervisor-metadata-item">
                                        <div class="supervisor-metadata-label">Tone to Use</div>
                                        <div class="supervisor-metadata-value">${escapeHtml(humanizeLabel(metadata.toneToUse))}</div>
                                    </div>
                                ` : ''}
                                ${hasIsMathsInAudio ? `
                                    <div class="supervisor-metadata-item">
                                        <div class="supervisor-metadata-label">Is Maths In Audio</div>
                                        <div class="supervisor-metadata-value">${metadata.isMathsInAudio ? 'Yes' : 'No'}</div>
                                    </div>
                                ` : ''}
                            </div>
                        ` : ''}
                        ${hasReasoningFields ? `
                            ${metadata.reasoningForAnswer ? `
                                <div class="supervisor-metadata-block">
                                    <div class="supervisor-metadata-label">Reasoning For Answer</div>
                                    <div class="supervisor-metadata-text">${escapeHtml(metadata.reasoningForAnswer)}</div>
                                </div>
                            ` : ''}
                            ${metadata.guidanceLevelReasoning ? `
                                <div class="supervisor-metadata-block">
                                    <div class="supervisor-metadata-label">Guidance Level Reasoning</div>
                                    <div class="supervisor-metadata-text">${escapeHtml(metadata.guidanceLevelReasoning)}</div>
                                </div>
                            ` : ''}
                        ` : ''}
                        ${hasGuidanceSteps ? `
                            <div class="supervisor-metadata-block">
                                <div class="supervisor-metadata-label">Guidance Steps</div>
                                <ul class="supervisor-metadata-list">
                                    ${metadata.guidanceSteps.map((step: any) => `<li>${escapeHtml(String(step))}</li>`).join('')}
                                </ul>
                            </div>
                        ` : ''}
                        ${hasShouldFillCanvasReason ? `
                            <div class="supervisor-metadata-block">
                                <div class="supervisor-metadata-label">Should Fill Canvas Reason</div>
                                <div class="supervisor-metadata-text">${escapeHtml(metadata.shouldFillCanvasReason)}</div>
                            </div>
                        ` : ''}
                        ${hasReasoningSummary ? `
                            <div class="supervisor-metadata-block">
                                <div class="supervisor-metadata-label">Reasoning Summary</div>
                                <div class="supervisor-metadata-text">${escapeHtml(metadata.reasoningSummary)}</div>
                            </div>
                        ` : ''}
                    </div>
                    `;
                })() : ''}
            </div>
        `;
    }).join('') : '';

    const implicitAttemptsHtml = renderImplicitAttempts(implicitAttempts, formatTimestamp);

    const feedbackEntries = Array.isArray(session.questionFeedback) ? session.questionFeedback : [];
    const feedbackMeta = session.questionFeedbackMeta || null;
    const feedbackTimestampCandidate = feedbackMeta?.updatedAt ?? feedbackMeta?.createdAt ?? null;
    const feedbackTimestampLabel = feedbackTimestampCandidate ? formatTimestamp(feedbackTimestampCandidate) : null;
    const difficultyLabel = feedbackMeta?.difficultyLevel ? humanizeLabel(String(feedbackMeta.difficultyLevel)) : null;

    const feedbackListHtml = feedbackEntries.length
        ? feedbackEntries.map((entry, index) => `
            <div class="feedback-item">
                <div class="feedback-item-index">${index + 1}</div>
                <div class="feedback-item-content">
                    <div class="feedback-question">${escapeHtml(entry.question)}</div>
                    <div class="feedback-answer">${escapeHtml(humanizeLabel(entry.answer))}</div>
                </div>
            </div>
        `).join('')
        : '<div class="feedback-empty">No learner feedback submitted for this attempt.</div>';

    const feedbackCardHtml = `
            <div class="feedback-card">
                <div class="feedback-card-header">
                    <div class="feedback-card-title">Learner Feedback</div>
                    ${difficultyLabel ? `<span class="feedback-chip">${escapeHtml(difficultyLabel)}</span>` : ''}
                </div>
                ${feedbackTimestampLabel ? `<div class="feedback-meta">Updated ${escapeHtml(feedbackTimestampLabel)}</div>` : ''}
                <div class="feedback-list">
                    ${feedbackListHtml}
                </div>
            </div>
    `;
    const fermiAnalysis = attemptDetails?.fermiAnalysis || null;
    const teacherAnalysis = fermiAnalysis?.teacher_analysis ?? null;
    const studentAnalysis = fermiAnalysis?.student_analysis ?? null;
    const teacherSections = teacherAnalysis
        ? [
            renderAnalysisTextSection('Timeline', teacherAnalysis.timeline),
            renderAnalysisTextSection('Student concepts', teacherAnalysis.studentConcepts),
            renderAnalysisListSection('Tutor help provided', teacherAnalysis.tutorHelp),
            renderAnalysisListSection('Concept feedback', teacherAnalysis.conceptFeedback),
            renderAnalysisListSection('Bottom line', teacherAnalysis.bottomLine),
        ]
        : [];
    const teacherCardHtml = teacherAnalysis
        ? buildAnalysisCard('Teacher Analysis', renderAnalysisHighlight(teacherAnalysis.status), teacherSections)
        : '';
    const studentSections = studentAnalysis
        ? [
            renderAnalysisListSection('What you did well', studentAnalysis.whatYouDidWell),
            renderAnalysisListSection('Keep in mind next time', studentAnalysis.keepInMindNextTime),
            renderAnalysisListSection('Practice suggestions', studentAnalysis.practiceSuggestion),
            renderAnalysisListSection('Concept feedback', studentAnalysis.conceptFeedback),
        ]
        : [];
    const studentCardHtml = studentAnalysis
        ? buildAnalysisCard('Student Analysis', null, studentSections)
        : '';
    const insightsPanelHtml = `<aside class="feedback-panel">${[feedbackCardHtml, teacherCardHtml, studentCardHtml].filter(Boolean).join('')}</aside>`;


    return `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Session Details - ${session.attemptId || session.socketId}</title>
                <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css" />
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                        margin: 0;
                        padding: 20px;
                        background: #f5f7fa;
                        color: #2c3e50;
                    }
                    .container {
                        max-width: 1200px;
                        margin: 0 auto;
                        background: white;
                        border-radius: 8px;
                        padding: 30px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    }
                    h1 {
                        margin-top: 0;
                        color: #2c3e50;
                        border-bottom: 2px solid #e2e8f0;
                        padding-bottom: 15px;
                    }
                    .session-details-container {
                        margin-bottom: 30px;
                        background: #f8fafc;
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        overflow: hidden;
                        position: relative;
                    }
                    .session-details-header {
                        padding: 20px;
                        background: #f1f5f9;
                        cursor: pointer;
                        border-bottom: 1px solid #e2e8f0;
                        transition: background-color 0.2s ease;
                    }
                    .session-details-header:hover {
                        background: #e2e8f0;
                    }
                    .session-details-header h2 {
                        margin: 0;
                        color: #2c3e50;
                        font-size: 18px;
                        font-weight: 600;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .toggle-icon {
                        font-size: 14px;
                        color: #667eea;
                        transition: transform 0.2s ease;
                        font-weight: 600;
                    }
                    .session-details-content {
                        padding: 20px;
                    }
                    .session-details-content.collapsed {
                        display: none;
                    }
                    .content-layout {
                        display: flex;
                        gap: 24px;
                        align-items: flex-start;
                        margin-top: 30px;
                    }
                    .content-main {
                        flex: 1 1 auto;
                        min-width: 0;
                    }
                    .feedback-panel {
                        flex: 0 0 320px;
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                        position: sticky;
                        top: 24px;
                    }
                    .feedback-card,
                    .analysis-card {
                        background: #ffffff;
                        border: 1px solid #e2e8f0;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
                    }
                    .feedback-card-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 12px;
                    }
                    .feedback-card-title {
                        font-weight: 700;
                        font-size: 16px;
                        color: #1f2937;
                    }
                    .feedback-chip {
                        background: #eef2ff;
                        color: #3730a3;
                        border-radius: 999px;
                        padding: 4px 10px;
                        font-size: 12px;
                        font-weight: 600;
                        letter-spacing: 0.02em;
                        white-space: nowrap;
                    }
                    .feedback-meta {
                        font-size: 12px;
                        color: #64748b;
                        margin-bottom: 16px;
                    }
                    .feedback-list {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    .feedback-item {
                        display: flex;
                        gap: 12px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 12px;
                        padding: 12px 14px;
                    }
                    .feedback-item-index {
                        font-weight: 700;
                        color: #4f46e5;
                        font-size: 14px;
                        min-width: 20px;
                        text-align: center;
                    }
                    .feedback-item-content {
                        flex: 1 1 auto;
                    }
                    .feedback-question {
                        font-size: 13px;
                        font-weight: 600;
                        color: #1e293b;
                        margin-bottom: 6px;
                    }
                    .feedback-answer {
                        display: inline-flex;
                        align-items: center;
                        padding: 4px 10px;
                        border-radius: 999px;
                        background: #e0f2fe;
                        color: #0f172a;
                        font-size: 12px;
                        font-weight: 600;
                    }
                    .feedback-empty {
                        color: #94a3b8;
                        font-size: 13px;
                        padding: 16px;
                        border-radius: 12px;
                        border: 1px dashed #cbd5f5;
                        background: #f8fafc;
                        text-align: center;
                    }
                    .analysis-card-title {
                        font-weight: 700;
                        font-size: 16px;
                        color: #1f2937;
                        margin: 0 0 12px;
                    }
                    .analysis-highlight {
                        background: #eef2ff;
                        border-radius: 12px;
                        padding: 12px;
                        font-weight: 600;
                        color: #312e81;
                        margin-bottom: 8px;
                        line-height: 1.5;
                    }
                    .analysis-section {
                        margin-top: 12px;
                    }
                    .analysis-section-label {
                        font-weight: 700;
                        font-size: 11px;
                        letter-spacing: 0.05em;
                        text-transform: uppercase;
                        color: #64748b;
                        margin-bottom: 6px;
                    }
                    .analysis-section-body {
                        font-size: 13px;
                        color: #0f172a;
                        line-height: 1.5;
                    }
                    .analysis-list {
                        margin: 0;
                        padding-left: 18px;
                        color: #0f172a;
                        font-size: 13px;
                        line-height: 1.5;
                    }
                    .analysis-list li {
                        margin-bottom: 6px;
                    }
                    .analysis-list li:last-child {
                        margin-bottom: 0;
                    }
                    @media (max-width: 1024px) {
                        .content-layout {
                            flex-direction: column;
                        }
                        .feedback-panel {
                            width: 100%;
                            position: static;
                        }
                    }
                    .validation-details {
                        position: absolute;
                        top: 16px;
                        right: 16px;
                        background: white;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        overflow: hidden;
                        max-width: 400px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                        z-index: 10;
                    }
                    .validation-summary {
                        cursor: pointer;
                        padding: 8px 12px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        list-style: none;
                        background: white;
                        transition: background-color 0.2s ease;
                    }
                    .validation-summary:hover {
                        background: #f8fafc;
                    }
                    .validation-summary::-webkit-details-marker {
                        display: none;
                    }
                    .validation-expand-icon {
                        font-size: 12px;
                        color: #667eea;
                        transition: transform 0.2s;
                        margin-left: 4px;
                    }
                    .validation-details[open] .validation-expand-icon {
                        transform: rotate(180deg);
                    }
                    .validity-badge {
                        padding: 6px 14px;
                        border-radius: 999px;
                        font-size: 12px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        background: #fee2e2;
                        color: #b91c1c;
                        border: 1px solid #fecaca;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                    }
                    .validity-badge.valid {
                        background: #dcfce7;
                        color: #15803d;
                        border-color: #bbf7d0;
                    }
                    .badge-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background: currentColor;
                    }
                    .validation-content {
                        padding: 12px;
                        border-top: 1px solid #e2e8f0;
                        background: #f8fafc;
                    }
                    .validation-item {
                        display: flex;
                        gap: 8px;
                        margin-bottom: 8px;
                        align-items: flex-start;
                    }
                    .validation-item:last-child {
                        margin-bottom: 0;
                    }
                    .validation-label {
                        font-weight: 600;
                        color: #64748b;
                        min-width: 120px;
                        font-size: 13px;
                        flex-shrink: 0;
                    }
                    .validation-value {
                        color: #1f2937;
                        font-size: 13px;
                    }
                    .validation-metadata-item {
                        flex-direction: column;
                        gap: 8px;
                    }
                    .validation-metadata {
                        background: white;
                        padding: 10px;
                        border-radius: 4px;
                        border: 1px solid #e2e8f0;
                        font-size: 12px;
                        font-family: Monaco, Menlo, monospace;
                        overflow-x: auto;
                        margin: 0;
                        color: #1f2937;
                        max-height: 300px;
                        overflow-y: auto;
                    }
                    .overview {
                        margin-bottom: 20px;
                        padding: 15px;
                        background: white;
                        border-radius: 6px;
                        border: 1px solid #e2e8f0;
                    }
                    .overview-heading {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 12px;
                        border-bottom: 1px solid #e2e8f0;
                        padding-bottom: 8px;
                        margin-bottom: 12px;
                    }
                    .overview-heading h3 {
                        margin: 0;
                        color: #2c3e50;
                        font-size: 16px;
                        font-weight: 600;
                    }
                    .refresh-controls {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        flex-wrap: wrap;
                        justify-content: flex-end;
                    }
                    .refresh-images-button {
                        background: #4f46e5;
                        color: white;
                        border: none;
                        border-radius: 6px;
                        padding: 6px 12px;
                        font-size: 13px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: background 0.2s ease, opacity 0.2s ease;
                    }
                    .refresh-images-button:hover {
                        background: #4338ca;
                    }
                    .refresh-images-button.is-loading {
                        cursor: wait;
                        opacity: 0.7;
                    }
                    .refresh-images-button:disabled {
                        cursor: not-allowed;
                        background: #cbd5f5;
                        color: #475569;
                        opacity: 0.8;
                    }
                    .refresh-status {
                        font-size: 12px;
                        color: #4b5563;
                        min-height: 1em;
                    }
                    .refresh-status.refresh-success {
                        color: #047857;
                    }
                    .refresh-status.refresh-error {
                        color: #b91c1c;
                    }
                    .langfuse-button {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.4rem;
                        background: white;
                        color: #4a5568;
                        border: 1px solid #e2e8f0;
                        padding: 6px 12px;
                        border-radius: 5px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        font-size: 13px;
                        text-decoration: none;
                    }
                    .langfuse-button:hover {
                        background: #f7fafc;
                        border-color: #cbd5e0;
                    }
                    .summary-content {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        gap: 12px;
                    }
                    .summary-title {
                        flex: 1;
                    }
                    .overview-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    }
                    .overview-panel {
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 12px;
                    }
                    .overview-title {
                        font-size: 14px;
                        font-weight: 700;
                        color: #475569;
                        margin-bottom: 8px;
                        text-transform: uppercase;
                    }
                    .overview-img {
                        width: 100%;
                        height: auto;
                        display: block;
                        border-radius: 6px;
                        border: 1px solid #e2e8f0;
                        background: white;
                    }
                    .overview-empty {
                        color: #64748b;
                        font-size: 13px;
                    }
                    .user-info, .session-info {
                        margin-bottom: 20px;
                        padding: 15px;
                        background: white;
                        border-radius: 6px;
                        border: 1px solid #e2e8f0;
                    }
                    .user-info:last-child, .session-info:last-child {
                        margin-bottom: 0;
                    }
                    .user-info h3, .session-info h3 {
                        margin-top: 0;
                        margin-bottom: 15px;
                        color: #2c3e50;
                        font-size: 16px;
                        font-weight: 600;
                        border-bottom: 1px solid #e2e8f0;
                        padding-bottom: 8px;
                    }
                    .info-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 20px;
                    }
                    .info-item {
                        display: flex;
                        flex-direction: column;
                    }
                    .info-label {
                        font-size: 12px;
                        color: #718096;
                        text-transform: uppercase;
                        margin-bottom: 5px;
                        font-weight: 600;
                    }
                    .info-value {
                        font-size: 16px;
                        font-weight: 600;
                        color: #2c3e50;
                    }
                    .cost-value {
                        font-family: monospace;
                        color: #48bb78;
                    }
                    .collapsible {
                        margin-top: 28px;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        background: white;
                        overflow: hidden;
                    }
                    .collapsible summary {
                        cursor: pointer;
                        padding: 16px 20px;
                        font-weight: 600;
                        color: #1f2937;
                        background: #f8fafc;
                        list-style: none;
                        outline: none;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .collapsible summary::-webkit-details-marker {
                        display: none;
                    }
                    .collapsible summary::after {
                        content: '▼';
                        font-size: 12px;
                        color: #667eea;
                        transition: transform 0.2s ease;
                    }
                    .collapsible[open] summary::after {
                        transform: rotate(180deg);
                    }
                    .collapsible-body {
                        padding: 20px;
                        border-top: 1px solid #e2e8f0;
                    }
                    .filter-controls {
                        margin-bottom: 16px;
                        padding: 12px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        flex-wrap: wrap;
                    }
                    .filter-controls label {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        cursor: pointer;
                        font-size: 14px;
                        color: #2c3e50;
                    }
                    .filter-controls input[type="checkbox"] {
                        width: 18px;
                        height: 18px;
                        cursor: pointer;
                    }
                    .empty-state {
                        padding: 16px;
                        text-align: center;
                        color: #94a3b8;
                        border: 1px dashed #cbd5f5;
                        border-radius: 8px;
                        background: #f8fafc;
                    }
                    .implicit-slider {
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                    }
                    .implicit-slider-bar {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        gap: 12px;
                        padding: 12px 16px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                    }
                    .implicit-flow-nav {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .implicit-flow-btn {
                        width: 36px;
                        height: 36px;
                        border-radius: 999px;
                        border: 1px solid #cbd5f5;
                        background: white;
                        color: #1f2937;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: background 0.2s ease, border-color 0.2s ease;
                    }
                    .implicit-flow-btn:hover:not(:disabled) {
                        background: #eef2ff;
                        border-color: #a5b4fc;
                    }
                    .implicit-flow-btn:disabled {
                        cursor: not-allowed;
                        opacity: 0.4;
                    }
                    .implicit-flow-status {
                        min-width: 70px;
                        text-align: center;
                        font-weight: 600;
                        font-size: 14px;
                        color: #1f2937;
                    }
                    .implicit-filter-controls {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        flex-wrap: wrap;
                    }
                    .implicit-filter-toggle {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 13px;
                        color: #475569;
                        cursor: pointer;
                    }
                    .implicit-filter-toggle input {
                        width: 18px;
                        height: 18px;
                        cursor: pointer;
                    }
                    .implicit-slide-track {
                        min-height: 120px;
                    }
                    .implicit-slide {
                        display: none;
                    }
                    .implicit-slide.is-active {
                        display: block;
                    }
                    .implicit-slide.is-filtered {
                        display: none !important;
                    }
                    .implicit-filter-empty {
                        padding: 18px;
                        text-align: center;
                        color: #475569;
                        background: #f8fafc;
                        border: 1px dashed #cbd5f5;
                        border-radius: 8px;
                    }
                    .implicit-entry {
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        margin-bottom: 18px;
                        overflow: hidden;
                        background: #f8fafc;
                    }
                    .implicit-header {
                        background: #eef2ff;
                        padding: 16px;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .implicit-title {
                        font-weight: 600;
                        font-size: 15px;
                        color: #312e81;
                    }
                    .implicit-meta {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 12px;
                        margin-top: 6px;
                        font-size: 12px;
                        color: #4c51bf;
                    }
                    .implicit-meta code {
                        font-size: 12px;
                        background: rgba(99,102,241,0.12);
                        padding: 2px 4px;
                        border-radius: 4px;
                    }
                    .implicit-body {
                        padding: 16px;
                        background: white;
                    }
                    .implicit-info-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                        gap: 12px;
                        margin-bottom: 16px;
                    }
                    .implicit-info-item {
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 10px;
                    }
                    .implicit-label {
                        display: block;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        color: #64748b;
                        margin-bottom: 4px;
                    }
                    .implicit-value {
                        font-size: 13px;
                        font-weight: 600;
                        color: #1f2937;
                    }
                    .implicit-text-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 16px;
                        margin-top: 12px;
                    }
                    .implicit-text-block {
                        margin-top: 12px;
                    }
                    .implicit-subtitle {
                        font-size: 13px;
                        font-weight: 600;
                        color: #1f2937;
                        margin-bottom: 6px;
                    }
                    .implicit-image-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                        gap: 16px;
                        margin-top: 16px;
                    }
                    .implicit-image {
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 12px;
                        text-align: center;
                    }
                    .implicit-image img {
                        max-width: 100%;
                        border-radius: 4px;
                        border: 1px solid #cbd5f5;
                    }
                    .implicit-subdetails {
                        margin-top: 12px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 8px 12px;
                    }
                    .conversation-section {
                        margin-top: 30px;
                    }
                    .conversation-section h2 {
                        color: #2c3e50;
                        margin-bottom: 20px;
                    }
                    .message {
                        margin-bottom: 15px;
                        padding: 15px;
                        border-radius: 8px;
                        border-left: 4px solid;
                    }
                    .message.is-hidden {
                        display: none !important;
                    }
                    .message.user {
                        background: #e3f2fd;
                        border-left-color: #2196f3;
                    }
                    .message.assistant {
                        background: #e8f5e8;
                        border-left-color: #4caf50;
                    }
                    .message.supervisor {
                        background: #fff3e0;
                        border-left-color: #ff9800;
                    }
                    .message-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 10px;
                    }
                    .message-type {
                        font-weight: 600;
                        text-transform: capitalize;
                    }
                    .message-timestamp {
                        font-size: 12px;
                        color: #718096;
                    }
                    .message-text {
                        line-height: 1.6;
                        white-space: pre-wrap;
                    }
                    .message-section-title { margin-top: 8px; font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; }
                    .code-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px; white-space: pre-wrap; font-family: Monaco, Menlo, monospace; font-size: 12px; margin-top: 4px; }
                    .implicit-line-list { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; margin-top: 8px; }
                    .implicit-line-item { display: grid; grid-template-columns: auto 1fr; gap: 8px; align-items: baseline; padding: 6px 0; border-bottom: 1px solid #e2e8f0; }
                    .implicit-line-item:last-child { border-bottom: none; }
                    .implicit-line-index { font-family: Monaco, Menlo, monospace; font-size: 12px; color: #64748b; }
                    .implicit-line-content { font-size: 13px; color: #1f2937; line-height: 1.6; }
                    .implicit-line-content .katex-display { margin: 12px 0; }
                    .implicit-line-content p { margin: 0; }
                    .implicit-solution-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 14px 16px; margin-top: 8px; font-size: 13px; color: #1f2937; line-height: 1.7; }
                    .implicit-solution-block .katex-display { margin: 12px 0; }
                    .implicit-rich-text { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; font-size: 13px; color: #1f2937; line-height: 1.6; min-height: 24px; }
                    .implicit-rich-text .katex-display { margin: 12px 0; }
                    .implicit-rich-text p { margin: 0; }
                    .message-board { margin-top: 6px; }
                    .message-board-content {
                        background: #0f172a;
                        color: #e2e8f0;
                        border-radius: 6px;
                        padding: 10px 12px;
                        font-size: 13px;
                        line-height: 1.6;
                        overflow-x: auto;
                    }
                    .message-board-content p { margin: 0 0 10px; }
                    .message-board-content p:last-child { margin-bottom: 0; }
                    .message-extra { margin-top: 8px; }
                    .kv { margin-top: 4px; }
                    .kv-row { display: flex; gap: 8px; margin: 2px 0; }
                    .kv-label { color: #64748b; min-width: 70px; }
                    .kv-value { color: #334155; }
                    .message-subtitle { font-weight: 600; color: #475569; margin-top: 6px; }
                    .message-image-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                        gap: 12px;
                        margin-top: 10px;
                    }
                    .message-image-grid details {
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 8px;
                    }
                    .message-image-grid summary {
                        cursor: pointer;
                        font-size: 13px;
                        font-weight: 600;
                        color: #4f46e5;
                    }
                    .message-image-grid img {
                        margin-top: 8px;
                        max-width: 100%;
                        border-radius: 4px;
                        border: 1px solid #cbd5f5;
                    }
                    .intent-msg { display: flex; align-items: baseline; gap: 6px; margin-top: 4px; }
                    .intent-badge { background: #e2e8f0; color: #334155; border-radius: 999px; padding: 2px 8px; font-size: 12px; font-weight: 700; }
                    .intent-badge.user { background: #dbeafe; color: #1e3a8a; }
                    .intent-badge.assistant { background: #dcfce7; color: #14532d; }
                    .intent-text { color: #334155; }
                    .message-attachment { display: inline-flex; align-items: center; gap: 6px; margin-top: 6px; padding: 6px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; }
                    .attachment-icon { font-size: 12px; }
                    .message-attachment a { color: #667eea; text-decoration: none; font-size: 13px; font-weight: 600; }
                    .message-attachment a:hover { text-decoration: underline; }
                    .supervisor-metadata {
                        margin-top: 12px;
                        padding: 12px;
                        background: #fef3c7;
                        border: 1px solid #fbbf24;
                        border-radius: 8px;
                    }
                    .supervisor-metadata-title {
                        font-weight: 700;
                        font-size: 13px;
                        color: #92400e;
                        margin-bottom: 10px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .supervisor-metadata-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 10px;
                        margin-bottom: 12px;
                    }
                    .supervisor-metadata-item {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }
                    .supervisor-metadata-label {
                        font-size: 11px;
                        font-weight: 600;
                        color: #78350f;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .supervisor-metadata-value {
                        font-size: 13px;
                        color: #1f2937;
                        font-weight: 500;
                    }
                    .supervisor-metadata-block {
                        margin-top: 10px;
                    }
                    .supervisor-metadata-text {
                        font-size: 13px;
                        line-height: 1.6;
                        color: #374151;
                        white-space: pre-wrap;
                        background: #fffbeb;
                        padding: 10px;
                        border-radius: 6px;
                        border: 1px solid #fde68a;
                    }
                    .supervisor-metadata-list {
                        margin: 0;
                        padding-left: 20px;
                        color: #374151;
                        font-size: 13px;
                        line-height: 1.6;
                    }
                    .supervisor-metadata-list li {
                        margin-bottom: 6px;
                    }
                    .supervisor-metadata-list li:last-child {
                        margin-bottom: 0;
                    }
                    .socket-id {
                        font-family: monospace;
                        background: #e2e8f0;
                        padding: 2px 6px;
                        border-radius: 4px;
                        /* Truncate long IDs nicely */
                        display: inline-block;
                        max-width: 360px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        vertical-align: bottom;
                    }
                </style>
            </head>
            <body>
                <div class="container" data-platform-url="${escapeHtml(PLATFORM_BASE_URL)}" data-auth-cookie="${escapeHtml(AUTH_COOKIE_NAME)}" data-bucket-name="${escapeHtml(STORAGE_BUCKET_NAME)}">
                    <h1>Session Details</h1>

                    <div class="session-details-container">
                        <div class="session-details-header" id="sessionDetailsHeader">
                            <h2><span class="toggle-icon" id="sessionToggleIcon">▶</span> Session Details</h2>
                        </div>
                        <div class="session-details-content collapsed" id="sessionDetailsContent">
                            ${attemptDetails ? `
                            <details class="validation-details">
                                <summary class="validation-summary">
                                    <span class="validity-badge ${isAttemptValid ? 'valid' : 'invalid'}">
                                        <span class="badge-dot"></span>
                                        ${isAttemptValid ? 'Valid' : 'Invalid'}
                                    </span>
                                    <span class="validation-expand-icon">▼</span>
                                </summary>
                                <div class="validation-content">
                                    <div class="validation-item">
                                        <span class="validation-label">Status:</span>
                                        <span class="validation-value">${escapeHtml(attemptStatusLabel)}</span>
                                    </div>
                                    <div class="validation-item">
                                        <span class="validation-label">Beta Validation:</span>
                                        <span class="validation-value">${attemptDetails.betaValidation ? 'True' : 'False'}</span>
                                    </div>
                                    ${validationMetadataHtml}
                                </div>
                            </details>
                            ` : ''}
                            ${session.user ? `
                            <div class="user-info">
                                <h3>User Details</h3>
                                <div class="info-grid">
                                    <div class="info-item">
                                        <div class="info-label">User Name</div>
                                        <div class="info-value">${session.user.name || 'Unknown'}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">User Email</div>
                                        <div class="info-value">${session.user.email || 'Unknown'}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">User ID</div>
                                        <div class="info-value socket-id" title="${session.user.id || ''}">${session.user.id || 'Unknown'}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Grade</div>
                                        <div class="info-value">${session.user.grade || 'Unknown'}</div>
                                    </div>
                                </div>
                            </div>
                            ` : ''}

                            <div class="session-info">
                                <h3>Session Information</h3>
                                <div class="info-grid">
                                    <div class="info-item">
                                        <div class="info-label">Attempt ID</div>
                                        <div class="info-value socket-id" title="${session.attemptId || ''}">${session.attemptId || 'Unknown'}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Socket ID</div>
                                        <div class="info-value socket-id" title="${session.socketId || ''}">${session.socketId || 'Unknown'}</div>
                                    </div>
                                    ${attemptDetails ? `
                                        <div class="info-item">
                                            <div class="info-label">Attempt Status</div>
                                            <div class="info-value">${escapeHtml(attemptStatusLabel)}</div>
                                        </div>
                                        <div class="info-item">
                                            <div class="info-label">Beta Validation</div>
                                            <div class="info-value">${attemptDetails.betaValidation ? 'True' : 'False'}</div>
                                        </div>
                                    ` : ''}
                                    <div class="info-item">
                                        <div class="info-label">Total Cost</div>
                                        <div class="info-value cost-value">${formatCost(session.costs.totalCost)}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">${(session.costs.realtime && session.costs.realtime.totalCost > 0) ? 'OpenAI Realtime Cost' : 'Gemini Live Cost'}</div>
                                        <div class="info-value cost-value">${formatCost((session.costs.realtime && session.costs.realtime.totalCost > 0) ? session.costs.realtime.totalCost : ((session.costs.geminiLive && session.costs.geminiLive.totalCost) || 0))}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Supervisor Cost</div>
                                        <div class="info-value cost-value">${formatCost(session.costs.tutor.totalCost)}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Implicit Checker Cost</div>
                                        <div class="info-value cost-value">${formatCost((session.costs.implicitChecker && session.costs.implicitChecker.totalCost) || 0)}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Implicit Attempts</div>
                                        <div class="info-value">${session.implicitAttemptCount != null ? session.implicitAttemptCount : implicitAttempts.length}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Duration</div>
                                        <div class="info-value">${session.duration}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Start Time</div>
                                        <div class="info-value">${session.startTime}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">End Time</div>
                                        <div class="info-value">${session.endTime || 'Unknown'}</div>
                                    </div>
                                    <div class="info-item">
                                        <div class="info-label">Messages</div>
                                        <div class="info-value">${session.messageCount || 0}</div>
                                    </div>
                                </div>
                            </div>

                            <div class="overview">
                                <div class="overview-heading">
                                    <h3>Overview</h3>
                                    <div class="refresh-controls">
                                        <button type="button" class="refresh-images-button" id="refreshSolutionImages">Refresh images</button>
                                        <span class="refresh-status" id="refreshImageStatus" role="status" aria-live="polite"></span>
                                    </div>
                                </div>
                                <div class="overview-grid">
                                    <div class="overview-panel">
                                        <div class="overview-title">Question</div>
                                        ${latestQuestionUrl ? `<img class="overview-img" src="${latestQuestionUrl}" alt="Question image">` : `<div class="overview-empty">No question image</div>`}
                                    </div>
                                    <div class="overview-panel">
                                        <div class="overview-title">Solution</div>
                                        ${latestSolutionUrl ? `<img class="overview-img" data-solution-image="true" src="${latestSolutionUrl}" alt="Solution image">` : `<div class="overview-empty">No solution image</div>`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="content-layout">
                        <div class="content-main">
                            <details class="collapsible" open>
                                <summary>
                                    <span class="summary-title">Conversation History</span>
                                    ${LANGFUSE_TUTOR_PROJECT_ID && session.attemptId ? `
                                        <a href="https://us.cloud.langfuse.com/project/${LANGFUSE_TUTOR_PROJECT_ID}/sessions/${session.attemptId}" target="_blank" rel="noopener noreferrer" class="langfuse-button" onclick="event.stopPropagation();" title="View Langfuse trace for this attempt">
                                            Langfuse Trace
                                            <img src="/langfuse-color.svg" alt="Langfuse" style="height: 1em; width: 1em;">
                                        </a>
                                    ` : ''}
                                </summary>
                                <div class="collapsible-body">
                                    <div class="filter-controls">
                                        <label>
                                            <input type="checkbox" id="filterToggle">
                                            <span>Show only input/output</span>
                                        </label>
                                        <span style="color: #718096; font-size: 12px;">(Hides function and intent calls)</span>
                                    </div>
                                    ${conversationHtml || '<div class="empty-state">No conversation history for this attempt.</div>'}
                                </div>
                            </details>

                            <details class="collapsible" ${implicitAttempts.length ? 'open' : ''}>
                                <summary>Implicit Attempts History (${implicitAttempts.length})</summary>
                                <div class="collapsible-body">
                                    ${implicitAttemptsHtml}
                                </div>
                            </details>
                        </div>
                        ${insightsPanelHtml}
                    </div>
                </div>

                <script>
${buildSessionInteractionsScript(PLATFORM_BASE_URL, AUTH_COOKIE_NAME, STORAGE_BUCKET_NAME)}
                </script>
            </body>
            </html>
        `;
}

export async function openSessionDetailsWindow(
    session: SessionData,
    SERVER_URL: string,
    environment?: string
) {
    // Fetch the conversation lazily when a session is clicked
    let conversation: any[] = [];
    let implicitAttempts: ImplicitAttemptRecord[] = [];
    try {
        const identifier = session.attemptId || session.socketId || '';
        if (identifier) {
            const endpoint = `/api/conversations/${identifier}`;
            const payload = await apiClient.get<any>(endpoint, PLAYGROUND);
            conversation = Array.isArray(payload?.conversation)
                ? payload.conversation
                : Array.isArray(payload)
                    ? payload
                    : [];
            implicitAttempts = Array.isArray(payload?.implicitAttempts) ? payload.implicitAttempts : [];
            const feedbackDoc = payload?.questionFeedback || null;
            const normalizeFeedbackEntry = (entry: any): FeedbackEntry | null => {
                if (!entry || typeof entry !== 'object') return null;
                const questionText = entry.question != null ? String(entry.question) : '';
                const answerText = entry.answer != null ? String(entry.answer) : '';
                if (!questionText && !answerText) return null;
                return { question: questionText, answer: answerText };
            };
            const feedbackEntries = feedbackDoc && Array.isArray(feedbackDoc.feedbackQuestions)
                ? feedbackDoc.feedbackQuestions
                    .map(normalizeFeedbackEntry)
                    .filter((item: FeedbackEntry | null): item is FeedbackEntry => Boolean(item))
                : [];
            session.questionFeedback = feedbackEntries;
            session.questionFeedbackMeta = feedbackDoc
                ? {
                    difficultyLevel: feedbackDoc.difficultyLevel ?? null,
                    createdAt: feedbackDoc.createdAt ?? null,
                    updatedAt: feedbackDoc.updatedAt ?? null,
                }
                : null;
        }
    } catch (e) {
        // Ignore; show window with what we have
    }

    if (!session.questionFeedback) {
        session.questionFeedback = [];
    }
    if (session.questionFeedbackMeta === undefined) {
        session.questionFeedbackMeta = null;
    }

    const sessionHtml = generateSessionHTML(session, conversation, implicitAttempts);

    const sessionWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    if (sessionWindow) {
        sessionWindow.document.write(sessionHtml);
        sessionWindow.document.close();
    }
}
